import { CHAT_STATE, Constants } from "../model/Constants";
import { DVV_CALL_TYPE } from "./webrtc/Constants";
import pubSubGlobal from "../util/pubSubGlobal";
import ModalMgr from "../util/ModalMgr";
import {layoutRender} from "./templates/renders/layout";

/**
 * Creates the top most Chat window layout custom element
 * @type {{template, el: string, refId: string}}
 */
const Layout: any = {
    el: "#si-app",
    refId: "messaging-ui",
    renderer: layoutRender,
    created() {
        this.layoutEl = this.el.firstElementChild;

        if (this.$app.getSDKInstance().isPersistentWindow()) {
            this.layoutEl.classList.add("persistent-box");
        }
    },

    /**
     * Handles a keydown event on Chat Window
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
        this.$app.handleAccessKeyPress(event);
    },

    /**
     * Creates video call container element in messaging box
     * @returns {VideoContainer}
     */
    displayVideoCallContainer(data, reconnect) {
        if (this.videoContainer) {
            this.layoutEl.removeChild(this.videoContainer);
        }

        this.videoContainer = document.createElement("video-container-view");
        const enableCustomerCamera = !!(data && data.enabledCustomerCamera);

        if (data) {
            this.videoContainer.setAttribute("videoEnabled", data.isVideoEnabled);
            this.videoContainer.setAttribute(Constants.ENABLED_CUSTOMER_CAMERA, enableCustomerCamera);
            this.videoContainer.setAttribute("reconnect", reconnect);

            if (data.enableVoice) {
                this.videoContainer.setAttribute(Constants.ENABLED_VOICE_ONLY_CALL, data.enableVoice);
            }
        }

        this.layoutEl.append(this.videoContainer);
        return this.videoContainer;
    },

    /**
     * remove video container from messaging window
     */
    removeVideoCallContainer(state) {
        if (this.videoContainer) {
            this.layoutEl.removeChild(this.videoContainer);
            this.videoContainer = null;
        }

        if (typeof state !== "undefined") {
            const videoAudioCallInvitation = parseInt(this.$app
                .getPersistedValue(Constants.VIDEO_AUDIO_CALL_INVITATION, 0), 10);
            let dvvType = DVV_CALL_TYPE.VIDEO_CALL_ONE_WAY;

            if (videoAudioCallInvitation === Constants.CALL_STATE_VIDEO_INVITE) {
                dvvType = DVV_CALL_TYPE.VIDEO_CALL_TWO_WAY;
            } else if (videoAudioCallInvitation === Constants.CALL_STATE_VOICE_INVITE) {
                dvvType = DVV_CALL_TYPE.VOICE_ONLY;
            }

            /* logging web rtc event for declining call invitation */
            this.$app.logWebRTCEvents(state, dvvType);
        }
        this.$app.removePersistedValueByKey(Constants.VIDEO_AUDIO_CALL_INVITATION);
        this.$app.getStateMgr().setState({ state: CHAT_STATE.ENGAGED });
    },

    /**
     * returns video call container element
     * @return {VideoContainer}
     */
    getVideoContainer() {
        return this.videoContainer;
    },

    /**
     * Shows the Close Chat confirmation modal
     * by changing the state
     */
    showCloseModal() {
        this.setModalVisible();
        const closeModalView = document.createElement("close-modal-view");
        this.layoutEl.appendChild(closeModalView);
        ModalMgr.setFocusWithinModalWindow(closeModalView);
    },

    /**
     * Shows Email Modal
     */
    showEmailModal() {
        this.setModalVisible();
        const emailModalView = document.createElement("email-modal-view");
        this.layoutEl.appendChild(emailModalView);
        ModalMgr.setFocusWithinModalWindow(emailModalView);
    },

    /**
     * show info modal
     * @param {string}  html string
     */
    showInfoModalView(content) {
        this.setModalVisible();
        const infoModalView = document.createElement("info-modal-view");
        this.layoutEl.appendChild(infoModalView);
        this.setInfoModalBodyContent(content);
        ModalMgr.setFocusWithinModalWindow(infoModalView);
    },

    /**
     * Will set content of modal body of  info modal
     * @param {string}  html string
     */
    setInfoModalBodyContent(content) {
        const target = document.querySelector("info-modal-view .modal-body");
        const temp = document.createElement("div");
        temp.innerHTML = content;
        target.appendChild(temp);
    },

    /**
     * Shows Rich Media File Upload
     */
    showFileUploadModal() {
        this.setModalVisible();
        const fileModalView = document.createElement("file-upload-modal-view");
        this.layoutEl.appendChild(fileModalView);
        ModalMgr.setFocusWithinModalWindow(fileModalView);
    },

    /**
     * Hides Rich Media File Upload
     */
    hideFileUploadModal() {
        const fileModalView = document.querySelector("file-upload-modal-view");

        if (fileModalView) {
            this.layoutEl.removeChild(fileModalView);
        }
    },

    /**
     * Handle Layout click
     * @param {MouseEvent|TouchEvent} clickEvent	Mouse click, or touch event
     * @fires Layout#onLayoutClick
     */
    onLayoutClick(clickEvent) {
        /**
         * @event Layout#onLayoutClick
         * @type {MouseEvent|TouchEvent}
         */
        pubSubGlobal.publish("Layout.onClick", clickEvent);
    },
    setSurveyVisibility(visibility) {
        this.$app.shoutui.resetTopBar();
        this.data.survey = visibility;
    },
	/**
	 *
	 * @return {Promise<void>|*}
	 */
	renderSurvey() {
	    const uiconfig = this.$app.getUIConfig();
		if (uiconfig.pcsID && this.$app.isPreChatSurveyNeed()) {
            this.data.survey = true;
            this.surveyEl = this.layoutEl.querySelector("survey-view");

            return this.surveyEl.render().then(engageParams => {
                if (engageParams && engageParams.webrtc) {
                    // todo: display webrtc disclaimer window
                }
                return Promise.resolve(engageParams);
            }).then(engageParams => {
                this.setSurveyVisibility(false);
                return engageParams;
            });
		} else {
            return Promise.resolve();
        }
	},
	renderPostChatSurvey(id, data, asyncData) {
		this.data.survey = true;
		this.surveyEl = this.layoutEl.querySelector("survey-view");
		this.surveyEl.renderPostChatSurvey(id, data, asyncData);
	},

	/**
	 * Allow survey to fire automaton aborted event
	 */
	invokeSurveyClose() {
		if (this.data.survey) {
			this.surveyEl.closeChat();
		}
	}
};

/**
 * @export Layout
 * @param {CIApp} appInstance
 * @return {{template, el: string, refId: string}}
 */
export default appInstance => {
    Layout.$app = appInstance;
    const appConfig = appInstance.getUIConfig();

    Layout.setModalVisible = function() {
        appInstance.getStateMgr().setState({ isModalVisible: true });
    };

    Layout.constants = {
        chatWindowTitle: appInstance.ariaStrings.chatWindowTitle,
        style: appConfig.frameStyles || {},
        extendedSurveyFooterVisible: appInstance.extendedSurveyFooterVisible,
    };

    Layout.data = { survey: false };

    return Layout;
};
